import { LoadingButton } from '@mui/lab'
import { Stack } from '@mui/material'
import { isAxiosError } from 'axios'
import React, { useEffect } from 'react'

import { TestDynatracePreFlightCheckResultNotification } from 'components/status-checks/form/status-check/dynatrace/TestDynatracePreFlightCheckResultNotification'
import {
  isDynatraceSelectorEntityIdType,
  isDynatraceSelectorProblemsUrlType
} from 'components/status-checks/status-checks-dynatrace-form-utils'
import { type StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import toast from 'lib/toast'
import { type DynatracePreFlightSuccessResponse } from 'queries/status-checks/pre-flight/types'
import { useDynatracePreFlightCheck } from 'queries/status-checks/pre-flight/use-dynatrace-pre-flight-check'
import {
  isPreFlightEntityNotFoundError,
  isPreFlightErrorResponse
} from 'queries/status-checks/pre-flight/utils'
import { getMessageFromResponseOrError } from 'utils/strings'

export const TestDynatracePreFlightButton: React.FC<
  Pick<StatusCheckFormValues, 'externalIntegration' | 'dynatraceSelector'> & {
    isDisabled?: boolean
    // To represent a callback fired when a true error condition is encountered during pre-flight check
    onError(error: unknown): void
    // To represent a callback fired when no matching Dynatrace entity is found
    onFailure(): void
    onReset(): void
    onSuccess(response: DynatracePreFlightSuccessResponse): void
  }
> = ({
  dynatraceSelector,
  externalIntegration,
  isDisabled,
  onError,
  onFailure,
  onReset,
  onSuccess
}) => {
  const {
    error,
    isLoading,
    isSuccess,
    mutateAsync: queryDynatracePreFlightCheck,
    reset: resetDynatracePreflightCheck
  } = useDynatracePreFlightCheck()

  /*
   * Performed in the Dynatrace IHC Observability Tool Type case. We don't hit the usual
   * 'endpointConfiguration' check here as we do with most other HC types, but rather a
   * separate 'pre-fetch' endpoint to validate whether the Dynatrace Entity exists.
   */
  const handlePreFlightCheckRequest = async () => {
    if (
      !externalIntegration ||
      !dynatraceSelector ||
      isDynatraceSelectorProblemsUrlType(dynatraceSelector)
    )
      return

    // We want everything about the externalIntegration besides the `type` field to affixed to the request body.
    const { type: _type, ...teamExternalIntegrationReference } =
      externalIntegration
    /*
     * Our payload varies on the Dynatrace entity selection source chosen by the user
     * NOTE: we branch in this fashion explicitly so TS can infer the proper payload type
     */
    const payload = {
      teamExternalIntegrationReference,
      ...(isDynatraceSelectorEntityIdType(dynatraceSelector)
        ? { entityId: dynatraceSelector.entityId }
        : { k8sQuery: dynatraceSelector.k8sQuery })
    }

    try {
      const response = await queryDynatracePreFlightCheck(payload)

      onSuccess(response)
    } catch (error: unknown) {
      // Swallow known error types here as we display this in the UI in-line
      if (
        isAxiosError(error) &&
        isPreFlightErrorResponse(error.response?.data)
      ) {
        // In the case of an undiscovered entity, allow for HC creation to proceed
        if (isPreFlightEntityNotFoundError(error)) {
          onFailure()
        }

        return
      }

      toast({
        type: 'error',
        text: getMessageFromResponseOrError(
          error,
          'Failed to perform pre-flight check. Please try again'
        )
      })

      onError(error)
    }
  }

  /*
   * Changing the selector type should reset any pre-flight responses and any flags set
   * within the details context which may have validated previous request.
   */
  useEffect(() => {
    resetDynatracePreflightCheck()
    onReset()
  }, [
    dynatraceSelector?.type,
    dynatraceSelector?.entityId,
    dynatraceSelector?.k8sQuery,
    onReset,
    resetDynatracePreflightCheck
  ])

  return (
    <Stack spacing={2}>
      <LoadingButton
        data-testid="test-status-check"
        disabled={isDisabled}
        loading={isLoading}
        size="small"
        sx={{ alignSelf: 'flex-start' }}
        variant="contained"
        onClick={handlePreFlightCheckRequest}
      >
        Test Health Check
      </LoadingButton>
      {isSuccess ? (
        <TestDynatracePreFlightCheckResultNotification isSuccess={isSuccess} />
      ) : (
        <TestDynatracePreFlightCheckResultNotification error={error} />
      )}
    </Stack>
  )
}
